import { derivePublicDomain } from "../hooks/derivePublicDomain";
import { config } from "../environment";
import {
  locationTenantDomain,
  tenantApiOrigin,
  tenantPublicOrigin,
} from "./tenantOrigin";

type TrustPolicy = {
  origins: Set<string>;
  // Hosts ending with this suffix are the tenant's per-space public
  // subdomains (e.g. `.acme.3b.run`, or `--acme.public-preview3b.com` on
  // a combined space-and-tenant hosts stack).
  spaceHostSuffix?: string;
};

// The origins a callbackURL may target. On a selfHostedSingleTenant stack the
// configured URLs name the seeded tenant, never the current one, so trust
// is rooted in the current tenant's derived hosts; off a tenant host there
// is no tenant domain and nothing beyond the current origin is trusted —
// this feeds a validator, so it narrows rather than throws. Single-tenant
// stacks trust the configured URLs and wildcard as-is.
function trustPolicy(): TrustPolicy {
  const origins = new Set<string>([window.location.origin]);
  if (!config.selfHostedSingleTenant && config.stackBaseDomain) {
    const tenantDomain = locationTenantDomain();
    if (!tenantDomain) return { origins };
    if (config.legacyApiSubdomain) {
      origins.add(tenantApiOrigin(config.stackBaseDomain, tenantDomain));
    }
    if (!config.stackPublicBaseDomain) return { origins };
    origins.add(tenantPublicOrigin(config.stackPublicBaseDomain, tenantDomain));
    const { wildcardDomain, combinedSpaceTenantLabel } = derivePublicDomain(
      config.publicWildcardDomain,
      config.stackPublicBaseDomain,
      config.selfHostedSingleTenant,
      config.combinedSpaceTenantHosts,
      tenantDomain
    );
    if (!wildcardDomain) return { origins };
    const tenantMarker = combinedSpaceTenantLabel
      ? `--${combinedSpaceTenantLabel}`
      : "";
    return { origins, spaceHostSuffix: `${tenantMarker}.${wildcardDomain}` };
  }
  for (const candidate of [config.externalApiUrl!, config.externalPublicUrl!]) {
    try {
      origins.add(new URL(candidate).origin);
    } catch {}
  }
  return {
    origins,
    spaceHostSuffix: config.publicWildcardDomain
      ? `.${config.publicWildcardDomain}`
      : undefined,
  };
}

/**
 * Reads the `callbackURL` query param and validates it against the set of
 * origins we trust, so a crafted link can't use the sign-in page as an
 * open redirect.
 */
export function readTrustedSignInCallbackURL(
  search: string = window.location.search
): string | null {
  const raw = new URLSearchParams(search).get("callbackURL");
  if (!raw) return null;

  let parsed: URL;
  try {
    parsed = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return null;
  }

  const { origins, spaceHostSuffix } = trustPolicy();
  if (origins.has(parsed.origin)) return parsed.toString();
  if (spaceHostSuffix && parsed.host.endsWith(spaceHostSuffix)) {
    return parsed.toString();
  }
  return null;
}
